import stringify from 'fast-json-stable-stringify'
import pick from 'lodash/pick'
import cloneDeep from 'lodash/cloneDeep'

import { AuthcoreVaultClient } from './vault'
import { encodeCosmosAddress, stripBOM, normalizeToCosmosAddress } from './utils'

/**
 * The Cosmos wallet provider.
 *
 * @public
 * @param {object} config
 * @param {AuthcoreVaultClient} config.client An AuthcoreVaultClient instance.
 * @param {string} [config.path=m/44'/118'/0'/0/0] The wallet derivation path.
 * @param {string} [config.oid=%%USER%%/hdwallet_default] The wallet derivation path.
 * @returns {AuthcoreCosmosProvider} The Cosmos wallet provider integrated with Authcore.
 */
class AuthcoreCosmosProvider {
  constructor (config) {
    if (!config.client) {
      throw new Error('an AuthcoreVaultClient instance is required')
    }
    this.client = config.client
    this.path = config.path || `m/44'/118'/0'/0/0`
    this.oid = config.oid || '%%USER%%/hdwallet_default'
    this.wallets = undefined
  }

  /**
   * Lists the owned addresses.
   *
   * @returns {Promise<string[]>} The list of owned accounts.
   */
  async getAddresses () {
    const wallets = await this._initWallets()
    const addresses = wallets.map(wallet => wallet.address)
    return addresses
  }

  /**
   * Lists the owned public keys.
   *
   * @returns {Promise<string[]>} The list of owned public keys.
   */
  async getPublicKeys () {
    const wallets = await this._initWallets()
    const publicKeys = wallets.map(wallet => wallet.publicKey)
    return publicKeys
  }

  /**
   * Get wallet by address.
   *
   * @param {*} address The address of the wallet.
   * @returns {Promise<object>} A wallet.
   */
  async getWalletByAddress (address) {
    if (!address) {
      throw new Error('address is missing')
    }

    const wallets = await this._initWallets()
    if (wallets.length === 0) {
      throw new Error('cannot retrieve account information')
    }

    const normalizedAddress = normalizeToCosmosAddress(address)
    const wallet = wallets.find(({ address }) => normalizedAddress === address)

    if (!wallet) {
      throw new Error('account not found')
    }

    return wallet
  }

  /**
   * Signs a payload.
   *
   * @param {object} data The payload to be broadcasted, with signature missing.
   * @param {object[]} data.msgs The message object.
   * @param {object} data.fee The fee object.
   * @param {string} data.memo The memo.
   * @param {string} data.chain_id The chain id for the Cosmos network.
   * @param {string} data.account_number The account number.
   * @param {string} data.sequence The nonce for the wallet.
   * @param {object[]} data.signatures The signatures.
   * @param {string} address The address of that wallet that is used to sign the message.
   * @returns {Promise<object>} The signed payload that is ready to be broadcasted.
   */
  async sign (data, address) {
    const wallet = await this.getWalletByAddress(address)

    const dataToSign = stringify(
      pick(data, [ 'fee', 'msgs', 'chain_id', 'account_number', 'sequence', 'memo' ])
    )
    const dataToSignHex = Buffer.from(dataToSign).toString('hex')
    const sigHex = await this.client.hdwalletCosmosSign(this.oid, this.path, dataToSignHex)
    const sig = Buffer.from(sigHex, 'hex').toString('base64')

    let dataWithSig = cloneDeep(data)
    dataWithSig['signatures'] = dataWithSig['signatures'] || []

    const signature = {
      'pub_key': {
        'type': 'tendermint/PubKeySecp256k1',
        'value': wallet.publicKey
      },
      'signature': sig
    }
    dataWithSig['signatures'].push(signature)
    return dataWithSig
  }

  /**
   * Signs stargate direct-sign payload.
   *
   * @param {object} data The Unit8Array payload to be broadcasted, with signature missing.
   * @param {string} address The address of that wallet that is used to sign the message.
   * @returns {Promise<object>} The signed payload that is ready to be broadcasted.
   */
  async directSign (data, address) {
    const wallet = await this.getWalletByAddress(address)

    const dataToSignHex = Buffer.from(data).toString('hex')
    const sigHex = await this.client.hdwalletCosmosSign(this.oid, this.path, dataToSignHex)
    const sig = Buffer.from(sigHex, 'hex').toString('base64')

    let signed = cloneDeep(data)
    const signature = {
      'pub_key': {
        'type': 'tendermint/PubKeySecp256k1',
        'value': wallet.publicKey
      },
      'signature': sig
    }
    const signatures = signed['signatures'] || []
    signatures.push(signature)
    return { signed, signatures }
  }

  /**
   * Export seed mnemonic. Requires export key capability.
   *
   * @returns {Promise<string>} The seed mnemonic.
   */
  async exportMnemonic () {
    const mnemonic = await this.client.hdwalletExportMnemonic(this.oid)
    return mnemonic
  }

  /**
   * Export derived private key. Requires export key capability.
   *
   * @returns {Promise<string>} The path-derived private key in hex.
   */
  async exportKey () {
    const privkey = await this.client.hdwalletExportKey(this.oid, this.path)
    return privkey
  }

  /**
   * Generates a hdwallet if it is not initialized. Returns wallets info.
   *
   * @private
   * @returns {Promise<string[]>} The list of wallets.
   */
  async _initWallets () {
    if (this.wallets) return this.wallets

    var derived
    try {
      derived = await this.client.hdwalletGetExtendedPublicKey(this.oid, this.path)
    } catch (e) {
      await this.client.hdwalletGenerate(this.oid)
      derived = await this.client.hdwalletGetExtendedPublicKey(this.oid, this.path)
    }

    // Cosmos address is a Bitcoin style address: RIPEMD160(SHA256(pubkey))
    const pubkey = Buffer.from(stripBOM(derived.publicKey), 'hex')
    const wallets = [{
      publicKey: pubkey.toString('base64'),
      address: encodeCosmosAddress('cosmos', pubkey)
    }]
    this.wallets = wallets
    return wallets
  }
}

export { AuthcoreCosmosProvider }
